import React, { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';
import {
  TextField,
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Avatar,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  useTheme,
  IconButton,
  Button,
} from '@mui/material';
import { debounce } from 'lodash';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import APIService from '../../services/APIService';
import { Patient } from '../../types/types';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { capitalize } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';
import { useEventTracking } from '@services/statsig';
import NewPatientModal from '../Patient/NewPatientModal';

interface NavSearchProps {
  onClose?: () => void;
  toolbarOpen?: boolean;
  onSearchTextChange?: (hasText: boolean) => void;
  setIsHovering?: (isHovering: boolean) => void;
}

interface SearchResult {
  patient_id: string;
  first_name: string;
  last_name: string;
  identifier?: string;
  type: 'patient';
}

const NavSearch: React.FC<NavSearchProps> = ({
  onClose,
  toolbarOpen,
  onSearchTextChange,
  setIsHovering,
}) => {
  const navigate = useNavigate();

  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const { patient: patientMetrics, search: searchMetrics } = useEventTracking();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [newPatientModalOpen, setNewPatientModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Handle mouse enter on search container
  const handleMouseEnter = () => {
    if (setIsHovering) {
      setIsHovering(true);
    }
  };

  // Handle mouse leave on search container
  const handleMouseLeave = () => {
    if (setIsHovering) {
      setIsHovering(false);
    }
  };

  // Add click outside detection
  useEffect(() => {
    if (!toolbarOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        // Make sure we're not clicking on the search icon
        !(event.target as HTMLElement).closest('[aria-label="search"]')
      ) {
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toolbarOpen, onClose]);

  // Focus input when toolbar opens
  useEffect(() => {
    if (toolbarOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [toolbarOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  // Notify parent when search text changes
  useEffect(() => {
    if (onSearchTextChange) {
      onSearchTextChange(searchTerm.trim().length > 0);
    }
  }, [searchTerm, onSearchTextChange]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && selectedIndex >= 0) {
      const listItems = resultsRef.current.querySelectorAll('li');
      if (listItems && listItems.length > selectedIndex) {
        listItems[selectedIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  // Search function to fetch patients based on search term
  const searchPatients = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setResults([]);
        setNoResults(false);
        return;
      }

      setLoading(true);
      setNoResults(false);

      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/search?q=${encodeURIComponent(term)}&page=1&per_page=5`,
          accessToken,
        });

        if (response.ok) {
          const data = response.value;
          const patientResults: SearchResult[] = data.patients.map((patient: Patient) => ({
            patient_id: patient.patient_id,
            first_name: patient.first_name,
            last_name: patient.last_name,
            identifier: patient.identifier,
            type: 'patient',
          }));

          setResults(patientResults);
          setNoResults(patientResults.length === 0);

          // Track search performed
          searchMetrics.performed({
            search_type: 'patient',
            search_context: 'nav_search',
            query_length: term.length,
            results_count: patientResults.length,
            has_results: patientResults.length > 0,
          });
        }
      } catch (error) {
        console.error('Error searching patients:', error);
        setResults([]);
        setNoResults(true);
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken],
  );

  // Create debounced search function
  const debouncedSearch = useRef(
    debounce((term: string) => {
      searchPatients(term);
    }, 300),
  ).current;

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const previousValue = searchTerm;
    setSearchTerm(value);
    
    // Track search initiated (only on first character)
    if (previousValue === '' && value !== '') {
      searchMetrics.initiated({
        search_type: 'patient',
        search_context: 'nav_search',
        character_count: value.length,
      });
    }
    
    debouncedSearch(value);
  };

  // Handle clearing the search input
  const handleClearSearch = () => {
    // Track search cleared
    if (searchTerm) {
      searchMetrics.cleared({
        search_type: 'patient',
        search_context: 'nav_search',
        had_results: results.length > 0,
        query_length: searchTerm.length,
      });
    }
    
    setSearchTerm('');
    setResults([]);
    setNoResults(false);
    setSelectedIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle result click
  const handleResultClick = (result: SearchResult, resultIndex?: number) => {
    if (result.type === 'patient') {
      // Construct a Patient object from the search result
      const patient: Patient = {
        patient_id: result.patient_id,
        first_name: result.first_name,
        last_name: result.last_name,
        identifier: result.identifier,
      } as Patient;

      // Track search result clicked
      searchMetrics.resultClicked({
        search_type: 'patient',
        search_context: 'nav_search',
        result_position: resultIndex !== undefined ? resultIndex + 1 : selectedIndex + 1, // 1-indexed
        result_id: patient.patient_id,
      });

      // Track patient selection from search
      patientMetrics.selected(patient.patient_id, {
        source: 'search',
        context: 'viewing',
      });

      navigate(`/patient/${patient.patient_id}`);
      onClose?.();
    }

    handleClearSearch();

    if (onClose) {
      onClose();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Only process keyboard navigation if we have results
    if (results.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        handleClearSearch();
        if (onClose) {
          onClose();
        }
        break;
    }
  };

  // Get initials for avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Handle opening the new patient modal
  const handleOpenNewPatientModal = () => {
    setNewPatientModalOpen(true);
  };

  // Handle new patient creation
  const handleNewPatientCreation = async (patientId: string) => {
    setNewPatientModalOpen(false);

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/patient/get?patient_id=${patientId}&origin=NavSearchNewPatientCreation`,
        accessToken,
      });

      if (response.ok) {
        const newPatient = response.value.patient;
        navigate(`/patient/${newPatient.patient_id}`);
        handleClearSearch();
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error selecting newly created patient:', error);
    }
  };

  return (
    <Box
      onKeyDown={handleKeyDown}
      ref={searchContainerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ width: '100%' }}
    >
      <TextField
        fullWidth
        placeholder={`Search ${capitalize(subjectLanguage)}s...`}
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={handleSearchChange}
        inputRef={inputRef}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '24px',
            bgcolor: 'transparent',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.light,
            },
          },
          '& .MuiInputBase-input': {
            bgcolor: 'transparent',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[300],
            backgroundColor: 'transparent',
          },
          backgroundColor: 'transparent',
        }}
       slotProps={{ input: {
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={20} /> : <SearchIcon />}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searchTerm && !loading && (
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        } }}/>

      {(results.length > 0 || noResults) && searchTerm.trim() !== '' && (
        <Paper
          elevation={3}
          sx={{
            marginTop: 1,
            maxHeight: '300px',
            overflow: 'auto',
            borderRadius: '8px',
            position: 'absolute',
            zIndex: 1300,
            width: '100%',
            left: 0,
            right: 0,
          }}
          ref={resultsRef}
        >
          {noResults ? (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                No results found for "{searchTerm}"
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleOpenNewPatientModal}
                sx={{ mt: 1 }}
              >
                Create new {subjectLanguage} "{searchTerm}"
              </Button>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              <ListItem sx={{ py: 1, px: 2, bgcolor: theme.palette.grey[100] }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {results.length} {results.length === 1 ? 'result' : 'results'} found
                </Typography>
              </ListItem>

              {results.map((result, index) => (
                <React.Fragment key={result.patient_id}>
                  <ListItemButton
                    onClick={() => handleResultClick(result, index)}
                    selected={index === selectedIndex}
                    sx={{
                      py: 1.5,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: theme.palette.action.hover,
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.action.selected,
                        '&:hover': {
                          bgcolor: theme.palette.action.selected,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          width: 40,
                          height: 40,
                          mr: 2,
                        }}
                      >
                        {getInitials(result.first_name, result.last_name)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {result.first_name} {result.last_name}
                        </Typography>
                        {result.identifier && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {result.identifier}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        icon={<PersonIcon fontSize="small" />}
                        label={capitalize(subjectLanguage)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  {index < results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      <NewPatientModal
        open={newPatientModalOpen}
        onClose={() => setNewPatientModalOpen(false)}
        onSuccessfulSubmission={handleNewPatientCreation}
        simplifiedView={true}
        initialName={searchTerm}
        source="nav_search"
      />
    </Box>
  );
};

export default NavSearch;
